import { useCallback, useEffect, useRef } from 'react';

import {
  FlyoutContainer,
  FlyoutTooltip,
} from 'components/shared/side-menu/flyout-container';
import {
  useHoverFlyout,
  useSideMenuPopoverTrigger,
} from 'components/shared/side-menu/hooks';
import { openSideMenuBadgePopover } from 'components/shared/side-menu/side-menu-badge-popover';
import { SIDE_MENU_POPOVER_GAP } from 'components/shared/side-menu/side-menu.constants';

import type { NavItem } from 'components/shared/side-menu/nav-config';

import BaseSideMenuBadge from './base-side-menu-badge';
import BaseNotificationBadge from './base-notification-badge';

interface BaseCollapsedTooltipProps {
  item: NavItem;
  baseItemClasses: string;
  isItemActive: boolean;
  renderLink: (props: Record<string, unknown>) => React.ReactNode;
  hasNotification?: boolean;
  onNavigate?: () => void;
}

const BaseCollapsedTooltip = ({
  item,
  baseItemClasses,
  isItemActive,
  renderLink,
  hasNotification = false,
  onNavigate,
}: BaseCollapsedTooltipProps) => {
  const { isHovered, flyoutTop, hoverProps } = useHoverFlyout();
  const flyoutRef = useRef<HTMLDivElement>(null);

  const getPopoverAnchorElement = useCallback(() => flyoutRef.current, []);
  const getPopoverFallbackAnchorElement = useCallback(
    () => hoverProps.ref.current,
    [hoverProps.ref]
  );

  const { trigger, scheduleOpenPopover, cancelScheduledOpen } =
    useSideMenuPopoverTrigger({
      popover: item.badge?.popover,
      getPopoverAnchorElement,
      getPopoverFallbackAnchorElement,
      anchorGap: SIDE_MENU_POPOVER_GAP.default,
    });

  useEffect(() => {
    if (!isHovered || !item.badge?.popover) return undefined;

    if (trigger === 'item') {
      scheduleOpenPopover();

      return cancelScheduledOpen;
    }

    openSideMenuBadgePopover(item.badge.popover, {
      anchorElement: flyoutRef.current,
      fallbackAnchorElement: hoverProps.ref.current,
      repositionOnly: true,
      anchorGap: SIDE_MENU_POPOVER_GAP.default,
    });

    return undefined;
  }, [
    isHovered,
    item.badge?.popover,
    trigger,
    scheduleOpenPopover,
    cancelScheduledOpen,
    hoverProps.ref,
  ]);

  const handleLinkClick = (): void => {
    cancelScheduledOpen();
    onNavigate?.();
  };

  const linkContent = (
    <span className="relative" aria-hidden="true">
      {item.icon}
      <BaseNotificationBadge hasNotification={hasNotification} />
    </span>
  );

  return (
    <div {...hoverProps} className="relative flex justify-center">
      {renderLink({
        to: item.to,
        onClick: handleLinkClick,
        className: baseItemClasses,
        'aria-label': item.label,
        'aria-current': isItemActive ? 'page' : undefined,
        children: linkContent,
        'data-testid': `side-menu-item-${item.id}`,
      })}

      <FlyoutContainer
        isVisible={isHovered}
        top={flyoutTop}
        alignItems="items-center"
        containerRef={flyoutRef}
      >
        <FlyoutTooltip>
          <span className="flex items-center gap-2">
            {item.label}
            <BaseSideMenuBadge
              badge={item.badge}
              getPopoverAnchorElement={getPopoverAnchorElement}
              getPopoverFallbackAnchorElement={getPopoverFallbackAnchorElement}
              anchorGap={SIDE_MENU_POPOVER_GAP.default}
            />
          </span>
        </FlyoutTooltip>
      </FlyoutContainer>
    </div>
  );
};

export default BaseCollapsedTooltip;
